<template>
  <AboutTopBox
    :background-image="{
      basePath: 'image/about_perq/bg_top',
      fileName: 'bg_top_sub_about02'
    }"
    :intro-image="{
      basePath: 'image/about_perq/intro',
      fileName: 'img_intro_about_pq'
    }"
    :title="$t('intro.perq.top.title')"
    :sub-title="$t('intro.perq.top.subTitle')"
    :context="$t('intro.perq.top.context')"
  />
  <!-- Section 1 -->
  <section
    class="relative flex items-center justify-center overflow-hidden bg-gradient-to-b from-slate-50 to-slate-100"
  >
    <div class="gn-container max-h-[840px] gap-7 overflow-hidden py-10 lg:gap-24 lg:py-[120px]">
      <div id="aos-about-perq1" data-aos="fade-up">
        <i18n-t keypath="intro.perq.section1.title" tag="p" class="gn-text-3xl font-bold">
          <template #mental>
            <span class="font-extrabold"> {{ $t('intro.perq.section1.mental') }}</span>
          </template>
          <template #perq>
            <span class="font-extrabold text-blue-500"> {{ $t('intro.perq.section1.perq') }}</span>
          </template>
        </i18n-t>
      </div>
      <div data-aos="fade-up" data-aos-delay="600" data-aos-anchor="#aos-about-perq1">
        <OptimizedImage
          base-path="image/about_perq/con1"
          file-name="img_about_brain"
          alt="img_about_brain"
          localized
          is-mobile
        />
      </div>
    </div>
  </section>
  <!-- Section 2 -->
  <section class="relative flex justify-center overflow-hidden">
    <div class="absolute inset-0 overflow-hidden">
      <div data-aos="aos-bg-scroll" class="z-0 h-[130%]">
        <OptimizedImage
          base-path="image/about_perq/con2"
          file-name="bg_about02_con01"
          alt="bg_about02_con01"
          img-format="jpg"
          image-class="size-full object-cover"
          localized
        />
      </div>
    </div>

    <div class="gn-container z-10 gap-7 py-10 lg:gap-24 lg:py-[120px]">
      <h1 id="aos-about-perq2" data-aos="fade-up" class="gn-text-3xl font-bold text-white">
        {{ $t('intro.perq.section2.title') }}
      </h1>
      <div
        class="grid w-full grid-cols-1 gap-3 sm:grid-cols-[repeat(auto-fit,minmax(350px,1fr))] md:grid-cols-[repeat(auto-fit,minmax(450px,1fr))]"
        :class="isEnglish ? 'xl:grid-cols-2' : '2xl:grid-cols-4'"
      >
        <div
          v-for="index in 4"
          :key="`expectation${index}`"
          data-aos="flip-left"
          data-aos-anchor="#aos-about-perq2"
          :data-aos-delay="400 * index + 200"
          class="inline-flex flex-row items-center gap-4 rounded-3xl border border-slate-300 bg-white/80 p-5 backdrop-blur-[10px] lg:flex-col lg:px-7 lg:py-10"
        >
          <img
            class="size-[60px] lg:size-20"
            :src="useDynamicImage(`svg/icon_expectation0${index}.svg`)"
            :alt="`icon_expectation0${index}`"
            loading="lazy"
          />
          <div class="flex grow flex-col items-start gap-3 lg:items-center lg:gap-5">
            <h2 class="gn-text-2xl text-start font-extrabold lg:text-center">
              {{ $t(`intro.perq.section2.expectation${index}.title`) }}
            </h2>
            <div class="flex w-fit flex-col items-start gap-1 lg:gap-3.5">
              <div v-for="num in 3" :key="`context${num}`" class="flex items-start gap-2">
                <img
                  class="mt-1 size-2 md:mt-2 lg:size-3"
                  src="~/assets/svg/icon_check_black.svg"
                  alt="check"
                  loading="lazy"
                />
                <span class="gn-text-base text-start font-bold text-slate-600">
                  {{ $t(`intro.perq.section2.expectation${index}.context${num}`) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  <!-- Section 3 -->
  <Box background-color="gray">
    <article
      id="aos-about-perq3"
      class="flex w-full flex-col items-center gap-9 text-center sm:max-w-[520px] lg:max-w-[1400px] lg:gap-16"
    >
      <h1 id="aos-about-perq3-header" data-aos="fade-up" class="gn-text-3xl font-extrabold">
        {{ $t('intro.perq.section3.title') }}
      </h1>
      <!-- 카드 그리드 -->
      <div class="grid gap-1 self-stretch sm:grid-cols-2 sm:gap-2 lg:h-[400px] lg:grid-cols-5 lg:gap-4">
        <div
          v-for="num in 5"
          :key="`bg_about02_con03_0${num}`"
          data-aos="fade-up"
          :data-aos-delay="400 * num + 200"
          data-aos-anchor="#aos-about-perq3-header"
          class="relative flex items-end overflow-hidden px-5 py-9 backdrop-blur lg:px-6"
        >
          <OptimizedImage
            base-path="image/about_perq/con3"
            :file-name="`bg_about02_con03_0${num}`"
            :alt="`bg_about02_con03_0${num}`"
            img-format="jpg"
            image-class="absolute left-0 bottom-0 opacity-90 object-cover rounded-2xl size-full"
          />
          <p class="gn-text-2xl z-10 whitespace-pre-line text-start font-extrabold text-white">
            {{ $t(`intro.perq.section3.cardList1.text${num}`) }}
          </p>
        </div>
      </div>
    </article>
  </Box>
  <!-- Section 4 (HomeSection5와 동일) -->
  <HomeSection5 title-key="intro.perq.section4.title" title-bold-key="intro.perq.section4.parent" />
  <!-- Section 5 -->
  <section
    id="aos-about-perq5"
    class="relative flex w-full flex-col items-center self-stretch overflow-hidden bg-slate-100"
  >
    <OptimizedImage
      v-for="(pattern, idx) in bgPatterns"
      :key="`bg_pattern_${idx}`"
      base-path="image/home/bg_pattern"
      :file-name="pattern.fileName"
      :alt="pattern.fileName"
      :image-class="pattern.className"
    />
    <article
      id="aos-about-perq5"
      class="gn-container max-h-[840px] gap-7 overflow-hidden pt-10 lg:gap-[70px] lg:pt-[120px]"
    >
      <div id="aos-about-perq5-title" data-aos="fade-up">
        <i18n-t
          keypath="intro.perq.section5.title"
          tag="p"
          class="gn-text-3xl whitespace-pre-line font-bold text-slate-700"
        >
          <template #perq>
            <span class="font-extrabold">{{ $t('intro.perq.section5.perq') }}</span>
          </template>
          <template #guardiann>
            <span class="font-extrabold text-blue-500">{{ $t('intro.perq.section5.guardiann') }}</span>
          </template>
        </i18n-t>
      </div>

      <div data-aos="fade-up" data-aos-delay="600" data-aos-anchor="#aos-about-perq5-title">
        <div class="relative h-[248px] w-72 overflow-hidden lg:h-[508px] lg:w-[590px]">
          <OptimizedImage
            base-path="image/home/img_index_ui01"
            file-name="img_index_ui01"
            alt="img_index_ui01"
            localized
            class="absolute bottom-0 left-0 object-cover"
          />
        </div>
      </div>
    </article>
  </section>
  <!-- 가디앤 프로필 -->
  <Box v-if="!isEnglish">
    <div id="aos-about-perq-profile" data-aos="fade-up" class="inline-flex flex-col gap-3 text-center font-bold">
      <h3 class="gn-text-xl text-slate-600">{{ $t('intro.perq.team.subTitle') }}</h3>
      <h1 class="gn-text-3xl">{{ $t('intro.perq.team.title') }}</h1>
    </div>
    <div class="flex flex-col justify-center gap-7 md:flex-row md:gap-5">
      <div
        v-for="(num, index) in 3"
        :key="`dr_profile${num}`"
        data-aos="fade-up"
        data-aos-anchor="#aos-about-perq-profile"
        :data-aos-delay="400 * num + 200"
        class="flex flex-col items-center gap-4 self-stretch lg:gap-[30px]"
      >
        <div
          class="flex h-[150px] w-[320px] items-center overflow-hidden rounded-2xl sm:w-[220px] md:h-auto xl:w-[340px]"
        >
          <OptimizedImage
            base-path="image/about_perq/profile"
            :file-name="`img_dr_profile0${num}`"
            :alt="`${$t(`intro.perq.team.names`, index)} 프로필`"
            img-format="jpg"
          />
        </div>
        <div class="inline-flex w-full flex-col items-start gap-3 text-start md:gap-5">
          <span class="gn-text-2xl font-bold leading-none">
            {{ $t(`intro.perq.team.names`, index) }}
          </span>
          <span class="gn-text-base">
            {{ $t(`intro.perq.team.dr${num}`) }}
          </span>
        </div>
      </div>
    </div>
  </Box>

  <DownloadSection />
</template>

<script setup>
import Box from '~/components/about/ContainerBox.vue'
import OptimizedImage from '~/components/common/OptimizedImage.vue'
import {useDynamicImage} from '~/composables/useDynamicImage'

const {locale} = useI18n()
const isEnglish = computed(() => locale.value === 'en')

const bgPatterns = [
  {
    fileName: 'bg_pattern_ia',
    className: `absolute opacity-5
                -left-5 top-[-50px] w-[250px] h-auto
                lg:-left-10 lg:top-[-18px] lg:w-[446px]`
  },
  {
    fileName: 'bg_pattern_g',
    className: `absolute opacity-5
                right-0 w-[155px] bottom-0
                md:top-0 md:translate-y-1/2 lg:-right-10 lg:w-[300px]`
  },
  {
    fileName: 'bg_pattern_n',
    className: `absolute opacity-5
                -left-20 -bottom-24 w-40 h-auto
                md:-bottom-1/4 lg:left-[16%] lg:w-[319px]`
  }
]
</script>

<style lang="scss" scoped></style>
